import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  Observable,
  of,
  take,
} from 'rxjs';

import { GoogleApiService } from '@blaze-shared/connectors-data-access';
import { ConnectorConfiguration, GoogleServiceType } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
  SelectComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, ButtonComponent, PanelComponent } from '@blaze-shared/shared-ui';
import { mapObjectsToOptions, SelectOption } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import { ConnectorForm, GoogleSheetsConfiguration } from './google-sheets-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-google-sheets-connector-dialog',
  templateUrl: './google-sheets-connector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectComponent,
    DialogModule,
    AsyncPipe,
    EnvironmentIndicatorComponent,
    CopyConnectorConfigEnvButtonComponent,
    ButtonBarComponent,
    NgTemplateOutlet,
    ButtonComponent,
    IconsModule,
    ReactiveFormsModule,
    PanelComponent,
  ],
  host: { class: 'data-connector-dialog' },
})
export class GoogleSheetsConnectorDialogComponent
  extends BaseConnectorDialogComponent<GoogleSheetsConfiguration>
  implements OnInit
{
  private googleApiService = inject(GoogleApiService);

  private availableSpreadSheets$$ = new BehaviorSubject<SelectOption<string>[]>([]);

  serviceName = 'google';

  availableSpreadSheets$ = this.availableSpreadSheets$$.asObservable();
  connectorForm: FormGroup<ConnectorForm>;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      spreadsheetId: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.setupPhase$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        concatMap(() => this.loadSpreadsheets(this.authState)),
      )
      .subscribe((options) => {
        this.availableSpreadSheets$$.next(options);
      });

    this.availableSpreadSheets$
      .pipe(
        untilDestroyed(this),
        concatLatestFrom(() => this.connectorForm.controls.spreadsheetId.valueChanges),
      )
      .subscribe(([options, selectedSpreadSheetId]) => {
        if (options.some((option) => option.value === selectedSpreadSheetId)) {
          return;
        }
        this.connectorForm.controls.spreadsheetId.setValue('', { emitEvent: false });
      });

    this.withOAuth();
  }

  ngOnInit(): void {
    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);
    this.goToAuth({ reauthorize: true });
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): GoogleSheetsConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const authState = this.authState ?? String(connector?.configuration?.authState ?? '');
    const spreadsheetId = String(connector?.configuration?.spreadsheetId ?? '');

    return { connectorId, connectorName, authState, spreadsheetId };
  }

  fillForm(configuration: GoogleSheetsConfiguration): void {
    const { connectorId, connectorName, authState, spreadsheetId } = configuration;

    this.connectorId = connectorId;
    this.authState = authState;

    this.loadingDialog$$.next(true);
    this.loadSpreadsheets(authState)
      .pipe(untilDestroyed(this), take(1))
      .subscribe((options) => {
        this.connectorForm.setValue(
          {
            connectorName,
            spreadsheetId,
          },
          { emitEvent: false },
        );

        this.availableSpreadSheets$$.next(options);
        this.loadingDialog$$.next(false);
      });
  }

  loadSpreadsheets(authState: string): Observable<SelectOption<string>[]> {
    if (!authState || !this.appInstalled()) {
      return of([]);
    }

    return this.googleApiService.getSpreadsheets(authState).pipe(
      catchError((error) => {
        this.handleErrors(error);
        return of([]);
      }),
      map((spreadsheets) => mapObjectsToOptions(spreadsheets) ?? []),
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, spreadsheetId } = this.connectorForm.value;

    if (!connectorName || !spreadsheetId) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
        spreadsheetId,
      },
    );

    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.showDatasets.emit(connector);
        }
      });
  }

  protected override getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName, {
      googleServiceType: GoogleServiceType.Spreadsheets,
    });
  }
}
